/**
 * screens-catalog.ts — สินค้าและต้นทุน SKU (T092)
 *
 * - SKU ที่ไม่มีต้นทุน ณ วันที่อ้างอิง ขึ้นสีแดง + นับจำนวนไว้ด้านบน (CONTEXT §7 Missing cost)
 * - ต้นทุนเป็น effective-dated: ช่วงซ้อนกันบันทึกไม่ได้ (server ปฏิเสธ + แสดงข้อความ)
 * - alias ผูกรหัสสินค้าจากช่องทาง/ร้าน เข้า SKU canonical
 */
import type { Page } from './router.js';
import { getTenant } from './state.js';
import { fmtDate } from './format.js';
import * as api from './api.js';
import { mountTikTok } from './screens-orders.js';
import {
  esc,
  money,
  pageShell,
  dataTable,
  pill,
  val,
  opt,
  bindSubmit,
  errorBox,
  pagerHtml,
  bindPager,
  showDrawer,
  drawerHtml,
} from './ui.js';

type Row = Record<string, unknown>;

const CHANNELS: [string, string][] = [
  ['tiktok_shop', 'TikTok Shop'],
  ['shopee', 'Shopee'],
  ['lazada', 'Lazada'],
  ['facebook', 'Facebook'],
  ['line', 'LINE'],
];

const todayKey = (): string => new Date().toISOString().slice(0, 10);

export const skuCostPage: Page = {
  title: 'สินค้าและต้นทุน SKU',
  render: () =>
    pageShell(
      'สินค้าและต้นทุน SKU',
      `<section class="card">
        <h3>🔄 ซิงค์สินค้าจาก TikTok Shop</h3>
        <p class="foot-note" style="margin-top:0">ดึงสินค้า/SKU จากร้าน TikTok ที่เชื่อมไว้เข้ามาที่นี่อัตโนมัติ แล้วค่อยใส่ต้นทุน</p>
        <div id="tiktok-sku-sync"><p class="muted">การซิงค์ TikTok ใช้ได้ในโหมดข้อมูลจริง</p></div>
      </section>
      <section class="card">
        <h3>เพิ่มสินค้า/SKU ใหม่ (ด้วยมือ)</h3>
        <form id="sku-form" class="grid-form">
          <label>รหัส SKU<input name="skuCode" placeholder="CHB-001" required></label>
          <label>ชื่อสินค้า<input name="name" placeholder="ชาบาแพ็ก 3"></label>
          <label>หน่วย<input name="unit" placeholder="ชิ้น"></label>
          <div class="form-actions"><button type="submit">เพิ่ม SKU</button><span id="sku-msg" class="muted"></span></div>
        </form>
      </section>
      <section class="card">
        <form id="sku-filter" class="grid-form" role="search">
          <label>ค้นหา (รหัส/ชื่อ)<input name="search" type="search" placeholder="เช่น CHB"></label>
          <label>ต้นทุน ณ วันที่<input name="asOf" type="date" value="${todayKey()}"></label>
          <label class="check"><input name="missingCostOnly" type="checkbox"> แสดงเฉพาะ SKU ที่ยังไม่มีต้นทุน</label>
          <div class="form-actions"><button type="submit">กรอง</button><span id="filter-msg" class="muted"></span></div>
        </form>
        <div id="sku-list">กำลังโหลด…</div>
      </section>
      ${drawerHtml}`,
    ),
  mount(root) {
    if (!getTenant()) return;
    const listEl = root.querySelector<HTMLElement>('#sku-list')!;
    const filter = root.querySelector<HTMLFormElement>('#sku-filter')!;
    const state = { limit: 25, offset: 0 };

    const openSku = async (sku: api.SkuStatusRow, asOf: string): Promise<void> => {
      showDrawer(root, `SKU ${sku.skuCode}`, '<p class="muted">กำลังโหลด…</p>');
      const [{ items: history }, { items: aliases }, { items: shops }] = await Promise.all([
        api.listCost(sku.id),
        api.listAliases(sku.id),
        api.listShops(),
      ]);
      const historyTable = dataTable(
        ['ต้นทุน/หน่วย', 'เริ่มมีผล', 'สิ้นสุด', 'สถานะ'],
        history.map((h: Row) => ({
          cells: [
            money(h['unitCost'] ?? h['unit_cost']),
            esc(fmtDate(String(h['effectiveFrom'] ?? h['effective_from'] ?? ''))),
            h['effectiveTo'] ?? h['effective_to']
              ? esc(fmtDate(String(h['effectiveTo'] ?? h['effective_to'])))
              : '—',
            h['effectiveTo'] ?? h['effective_to'] ? pill('ปิดแล้ว') : pill('ใช้อยู่', 'ok'),
          ],
        })),
        'ยังไม่มีประวัติต้นทุน — SKU นี้ทำให้รายงานไม่สมบูรณ์',
      );
      const aliasTable = dataTable(
        ['ช่องทาง', 'ร้าน', 'รหัสจากช่องทาง', 'ชื่อจากช่องทาง'],
        aliases.map((a: Row) => ({
          cells: [
            esc(a['channel_code']),
            esc(a['shop_id']),
            esc(a['external_sku_id']),
            esc(a['external_name'] ?? '—'),
          ],
        })),
        'ยังไม่มีการผูกรหัสจากช่องทาง',
      );
      const shopOptions = shops
        .map((s: Row) => `<option value="${esc(s['id'])}">${esc(s['name'])}</option>`)
        .join('');
      showDrawer(
        root,
        `SKU ${sku.skuCode} — ${sku.name ?? 'ไม่มีชื่อ'}`,
        `<p class="muted">ต้นทุนที่มีผล ณ ${fmtDate(asOf)}: <strong>${
          sku.hasCost ? money(sku.unitCost) : '<span class="error">ยังไม่มีต้นทุน</span>'
        }</strong></p>
        <h4>ประวัติต้นทุนมาตรฐาน</h4>${historyTable}
        <form id="cost-form" class="grid-form">
          <label>ต้นทุนต่อหน่วยใหม่<input name="unitCost" inputmode="decimal" placeholder="12.50" required></label>
          <label>วันที่เริ่มมีผล<input name="effectiveFrom" type="date" required></label>
          <label>เหตุผล<input name="reason" placeholder="ปรับราคาซัพพลายเออร์"></label>
          <div class="form-actions"><button type="submit">เพิ่มต้นทุน</button><span id="cost-msg" class="muted"></span></div>
        </form>
        <p class="foot-note">ต้นทุนใหม่มีผลตั้งแต่วันที่กำหนดเท่านั้น — รายการเดิมยังใช้ต้นทุนเดิม (ไม่คำนวณย้อนหลัง) และช่วงต้นทุนห้ามซ้อนกัน</p>
        <h4>รหัสสินค้าจากช่องทาง (alias)</h4>${aliasTable}
        <form id="alias-form" class="grid-form">
          <label>ช่องทาง<select name="channelCode">${CHANNELS.map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}</select></label>
          <label>ร้าน<select name="shopId">${shopOptions || '<option value="">— ยังไม่มีร้าน —</option>'}</select></label>
          <label>รหัสจากช่องทาง<input name="externalSkuId" placeholder="TT-123456" required></label>
          <label>ชื่อจากช่องทาง<input name="externalName" placeholder="ชื่อที่แสดงในแพลตฟอร์ม"></label>
          <div class="form-actions"><button type="submit">ผูกรหัส</button><span id="alias-msg" class="muted"></span></div>
        </form>`,
      );
      const costForm = root.querySelector<HTMLFormElement>('#cost-form')!;
      bindSubmit(
        costForm,
        root.querySelector<HTMLElement>('#cost-msg')!,
        async (f) => {
          const body: Row = {
            skuId: sku.id,
            unitCost: val(f, 'unitCost'),
            effectiveFrom: val(f, 'effectiveFrom'),
          };
          const reason = opt(f, 'reason');
          if (reason) body['reason'] = reason;
          await api.addCost(body);
        },
        () => refresh(),
      );
      const aliasForm = root.querySelector<HTMLFormElement>('#alias-form')!;
      bindSubmit(aliasForm, root.querySelector<HTMLElement>('#alias-msg')!, async (f) => {
        const shopId = val(f, 'shopId');
        if (!shopId) throw new Error('ยังไม่มีร้านในกิจการนี้ — เพิ่มร้านก่อนผูกรหัส');
        const body: Row = {
          skuId: sku.id,
          shopId,
          channelCode: val(f, 'channelCode'),
          externalSkuId: val(f, 'externalSkuId'),
        };
        const name = opt(f, 'externalName');
        if (name) body['externalName'] = name;
        await api.createAlias(body);
      });
    };

    async function refresh(): Promise<void> {
      listEl.innerHTML = 'กำลังโหลด…';
      try {
        const asOf = val(filter, 'asOf') || todayKey();
        const missingOnly =
          (filter.elements.namedItem('missingCostOnly') as HTMLInputElement).checked;
        const res = await api.listSkus({
          ...(val(filter, 'search') ? { search: val(filter, 'search') } : {}),
          ...(missingOnly ? { missingCostOnly: true } : {}),
          asOf,
          limit: state.limit,
          offset: state.offset,
        });
        const banner =
          res.missingCostCount > 0
            ? `<div class="banner banner-danger"><span>⛔</span><div><strong>SKU ไม่มีต้นทุน ${res.missingCostCount} รายการ</strong> — รายงาน P&L จะถูกทำเครื่องหมายว่า “ข้อมูลยังไม่สมบูรณ์” จนกว่าจะกรอกต้นทุน</div></div>`
            : `<p class="muted">SKU ทุกตัวมีต้นทุนที่มีผล ณ ${fmtDate(asOf)}</p>`;
        listEl.innerHTML =
          banner +
          dataTable(
            ['รหัส SKU', 'ชื่อ', 'หน่วย', `ต้นทุน ณ ${fmtDate(asOf)}`, 'ช่วงต้นทุน', ''],
            res.items.map((s) => ({
              cls: s.hasCost ? undefined : 'row-danger',
              cells: [
                esc(s.skuCode),
                esc(s.name ?? '—'),
                esc(s.unit),
                s.hasCost
                  ? `<span class="num">${money(s.unitCost)}</span>`
                  : `<span class="error">ยังไม่มีต้นทุน</span>`,
                esc(s.historyCount),
                `<button class="mini" data-sku="${esc(s.id)}">ต้นทุน/alias</button>`,
              ],
            })),
            'ยังไม่มี SKU — เพิ่มสินค้าใหม่ด้านบน',
          ) +
          pagerHtml(res.total, state.limit, state.offset);
        bindPager(listEl, state, refresh);
        listEl.querySelectorAll<HTMLButtonElement>('button[data-sku]').forEach((b) =>
          b.addEventListener('click', () => {
            const sku = res.items.find((s) => s.id === b.dataset['sku']);
            if (sku) {
              void openSku(sku, asOf).catch((e: unknown) =>
                showDrawer(root, 'ต้นทุน SKU', errorBox(e)),
              );
            }
          }),
        );
      } catch (e) {
        listEl.innerHTML = errorBox(e);
      }
    }

    bindSubmit(
      root.querySelector<HTMLFormElement>('#sku-form')!,
      root.querySelector<HTMLElement>('#sku-msg')!,
      async (f) => {
        const body: Row = { skuCode: val(f, 'skuCode') };
        for (const k of ['name', 'unit']) {
          const v = opt(f, k);
          if (v) body[k] = v;
        }
        await api.createSku(body);
      },
      () => refresh(),
    );
    filter.addEventListener('submit', (e) => {
      e.preventDefault();
      state.offset = 0;
      void refresh();
    });
    void refresh();

    // แผงซิงค์ TikTok — ซิงค์เสร็จแล้วรีเฟรชรายการ SKU อัตโนมัติ
    const ttHost = root.querySelector<HTMLElement>('#tiktok-sku-sync');
    if (ttHost) void mountTikTok(ttHost, () => void refresh());
  },
};
